import { createContext, useContext, useEffect, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  getRedirectResult,
  signInWithEmailAndPassword,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  signOut,
} from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db, googleProvider, ALLOWED_HD } from '../firebase';

const AuthContext = createContext(null);

function isAllowedGoogleAccount(user) {
  if (!ALLOWED_HD) return true;
  return (user?.email || '').toLowerCase().endsWith(`@${ALLOWED_HD.toLowerCase()}`);
}

// 'countries' field: absent/empty → access to all markets.
// Values may be codes ('th') or names ('Thailand'); lowercased for matching.
function normCountries(v) {
  if (!Array.isArray(v) || v.length === 0) return null;
  return v.map(s => String(s).trim().toLowerCase());
}

// Derive flat allowedCategories from country_categories for the Sidebar filter.
// Returns null (unrestricted) if any country has an empty category list.
function deriveCategoriesFromCountryCats(cc) {
  if (!cc || typeof cc !== 'object' || Object.keys(cc).length === 0) return null;
  const all = [];
  for (const cats of Object.values(cc)) {
    if (!Array.isArray(cats) || cats.length === 0) return null; // at least one market unrestricted
    all.push(...cats);
  }
  return all.length > 0 ? [...new Set(all)] : null;
}

export function AuthProvider({ children }) {
  const [user,              setUser]              = useState(null);
  const [loading,           setLoading]           = useState(true);
  const [authError,         setAuthError]         = useState(null);
  const [allowedCategories, setAllowedCategories] = useState(null);
  const [allowedCountries,  setAllowedCountries]  = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  // Fetch user_permissions/{email} from Firestore.
  // country_categories: null (unrestricted) when the doc or field is missing.
  async function loadPermissions(email) {
    try {
      const snap = await getDoc(doc(db, 'user_permissions', email));
      if (snap.exists()) {
        const data = snap.data();
        const cc = data.country_categories && typeof data.country_categories === 'object'
          ? data.country_categories
          : null;
        return {
          categories: deriveCategoriesFromCountryCats(cc),
          countries:  normCountries(data.countries),
        };
      }
    } catch (e) {
      console.warn('loadPermissions error:', e?.code, e?.message);
    }
    return { categories: null, countries: null };
  }

  useEffect(() => {
    getRedirectResult(auth).catch(() => {});
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) { setUser(null); setAllowedCategories(null); setAllowedCountries(null); setMustChangePassword(false); setLoading(false); return; }

      const isGoogle = u.providerData.some(p => p.providerId === 'google.com');

      if (isGoogle) {
        if (!isAllowedGoogleAccount(u)) {
          await signOut(auth);
          setAuthError(`Please sign in with your @${ALLOWED_HD} account.`);
          setUser(null); setAllowedCategories(null); setAllowedCountries(null); setMustChangePassword(false); setLoading(false); return;
        }
        const perms = await loadPermissions(u.email);
        console.log('[Auth] Google user:', u.email, '| allowedCategories:', perms.categories, '| allowedCountries:', perms.countries);
        setAllowedCategories(perms.categories);
        setAllowedCountries(perms.countries);
        setMustChangePassword(false);
        setUser(u); setLoading(false); return;
      }

      // Email/password — check Firestore allowlist
      try {
        const snap = await getDoc(doc(db, 'omni_growth_users', u.email));
        if (!snap.exists()) {
          await signOut(auth);
          setAuthError('Your account is not authorised. Contact hello@eureka.ai.');
          setUser(null); setAllowedCategories(null); setAllowedCountries(null); setLoading(false); return;
        }
        const data = snap.data();
        const cc = data.country_categories && typeof data.country_categories === 'object'
          ? data.country_categories : null;
        const cats = deriveCategoriesFromCountryCats(cc);
        const ctys = normCountries(data.countries);
        console.log('[Auth] Email user:', u.email, '| allowedCategories:', cats, '| allowedCountries:', ctys);
        setAllowedCategories(cats);
        setAllowedCountries(ctys);
        setMustChangePassword(data.mustChangePassword === true);
        setUser(u); setLoading(false);
      } catch {
        await signOut(auth);
        setAuthError('Sign-in failed — account not found or access denied. Contact hello@eureka.ai.');
        setUser(null); setAllowedCategories(null); setAllowedCountries(null); setMustChangePassword(false); setLoading(false);
      }
    });
    return unsub;
  }, []);

  const signInWithGoogle = async () => {
    setAuthError(null);
    try {
      // authStateReady() ensures Firebase has finished initialising before we
      // open the popup — this prevents the race condition that causes the
      // "Pending promise was never set" internal assertion failure.
      await auth.authStateReady();
      const result = await signInWithPopup(auth, googleProvider);
      if (result?.user && !isAllowedGoogleAccount(result.user)) {
        await signOut(auth);
        setAuthError(`Please sign in with your @${ALLOWED_HD} account.`);
      }
    } catch (e) {
      if (e.code !== 'auth/popup-closed-by-user' && e.code !== 'auth/cancelled-popup-request') {
        setAuthError(e.code === 'auth/popup-blocked'
          ? 'Popup blocked — please allow popups and try again.'
          : `Sign-in error: ${e?.message}`);
      }
    }
  };

  const signInWithEmail = async (email, password) => {
    setAuthError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (e) {
      const c = e.code;
      if (c === 'auth/invalid-credential' || c === 'auth/user-not-found' || c === 'auth/wrong-password') {
        setAuthError('Incorrect email or password.');
      } else if (c === 'auth/too-many-requests') {
        setAuthError('Too many failed attempts. Please wait a few minutes and try again.');
      } else if (c === 'auth/user-disabled') {
        setAuthError('This account has been disabled. Contact hello@eureka.ai.');
      } else {
        setAuthError('Sign-in failed. Please try again or contact hello@eureka.ai.');
      }
    }
  };

  const completePasswordChange = async (currentPassword, newPassword) => {
    const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
    await reauthenticateWithCredential(auth.currentUser, credential);
    await updatePassword(auth.currentUser, newPassword);
    await updateDoc(doc(db, 'omni_growth_users', auth.currentUser.email), {
      mustChangePassword: false,
    });
    setMustChangePassword(false);
  };

  const logout = () => signOut(auth);

  return (
    <AuthContext.Provider value={{
      user, loading, authError,
      isEurekaUser: (user?.email || '').toLowerCase().endsWith('@eureka.ai'),
      isEmailUser: (user?.providerData || []).some(p => p.providerId === 'password'),
      allowedCategories,
      allowedCountries,
      mustChangePassword,
      completePasswordChange,
      signInWithGoogle, signInWithEmail, logout,
      clearAuthError: () => setAuthError(null),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
